/**
 * Time block definitions for the Find-Slots DRAWER and the web form's checkbox
 * labels. Hours are 24h, interpreted in the location's timezone.
 *
 * NOT THE DEFINITION THE AGENTS' SEARCHES USE, and the difference is real rather
 * than cosmetic. These bounds drive `generateCandidates` in
 * `slot-computation/engine.ts`, so a drawer search for "morning" generates
 * candidates between 08:00 and 12:00. The voice and SMS agents go through
 * `applyTimePreference` instead, which is open-ended at both outer edges — an
 * opening before eight is still morning to it, and one after eight in the
 * evening is still evening. That predicate's cut points live in
 * `packages/agent-core/src/scheduling/time-of-day.ts`, which is also what the
 * scheduling prompt and the `offer_slots` schema are rendered from, so the model
 * is never told a boundary its own searches do not apply (ADR-214).
 */
export const TIME_BLOCKS = {
  morning: { label: 'Morning', start: '08:00', end: '12:00' },
  afternoon: { label: 'Afternoon', start: '12:00', end: '17:00' },
  evening: { label: 'Evening', start: '17:00', end: '20:00' },
} as const;

export type TimeBlockKey = keyof typeof TIME_BLOCKS;

export const TIME_BLOCK_KEYS = Object.keys(TIME_BLOCKS) as TimeBlockKey[];

/**
 * The scoping an offerable-slots search requires: an appointment type. The type
 * is what gives a slot its length and booking rules, so it can't be left open.
 * Providers and locations are optional filters — left empty, the engine
 * searches every provider × location that holds open slots for the type. Every
 * scheduling surface (Find Slots form, slot-search hook, chat tool) gates on
 * this before calling the engine — keep the rule here so they can't drift.
 */
export interface OfferableSlotsScope {
  providerIds: string[];
  locationIds: string[];
  appointmentTypeId?: string | null;
}

export function isOfferableSlotsScopeComplete(scope: OfferableSlotsScope): boolean {
  return !!scope.appointmentTypeId;
}

/**
 * Map a surface's selected time blocks to the engine's `preferredTimeOfDay`.
 * The engine accepts one block or several (a slot matches if it falls in ANY of
 * them), so the blocks pass straight through; an empty selection means "no time
 * gate" and collapses to `undefined`.
 */
export function toPreferredTimeOfDay(
  timeBlocks: readonly TimeBlockKey[] | undefined
): TimeBlockKey[] | undefined {
  return timeBlocks && timeBlocks.length > 0 ? [...timeBlocks] : undefined;
}
